'use client';

import { useEffect, useState } from 'react';

import { X } from 'lucide-react';

import { Button } from '@naumu/ui/components/button';
import { cn } from '@naumu/ui/utils/cn';

import { getAnalyticsConsent, setAnalyticsConsent } from './analytics-consent';
import { CookiePreferencesDialog } from './cookie-preferences-dialog';

/**
 * Full-width, bottom-fixed cookie banner.
 *
 * Renders only while the user is undecided (getAnalyticsConsent() === null).
 * Starts hidden and reveals after mount once localStorage has been read, so
 * there's no SSR/hydration mismatch. Accept / Reject writes the decision
 * (which opts the live PostHog instance in/out) and hides the banner.
 * "Cookie preferences" opens the granular dialog; the X only hides the banner
 * for this page view (nothing is written, so capture stays opted out).
 *
 * `suppressed` defers the question without answering it: the banner renders
 * nothing, no decision is written and capture stays opted out, exactly as for
 * an undecided visitor. The host sets it while something at the bottom of the
 * screen must stay reachable (a call's controls); the banner appears as soon
 * as it is lifted.
 */
export const AnalyticsConsentBanner = ({ suppressed = false }: { suppressed?: boolean } = {}) => {
	const [visible, setVisible] = useState(false);
	const [prefsOpen, setPrefsOpen] = useState(false);

	useEffect(() => {
		if (getAnalyticsConsent() === null) setVisible(true);
	}, []);

	if (!visible || suppressed) return null;

	const decide = (granted: boolean) => {
		setAnalyticsConsent(granted);
		setVisible(false);
	};

	return (
		<>
			<div
				role="dialog"
				aria-label="Cookie consent"
				className={cn(
					'fixed inset-x-0 bottom-0 z-[9000]',
					'bg-surface shadow-floating',
				)}>
				<div className="flex flex-col gap-5 px-6 py-5 lg:flex-row lg:items-center lg:gap-8 lg:pr-14">
					<div className="min-w-0 flex-1 pr-8 lg:pr-0">
						<p className="text-[17px] font-semibold leading-tight text-neutral">We use cookies</p>
						<p className="mt-2 max-w-[520px] text-[14px] leading-relaxed text-neutral-muted">
							Cookies help this site function, measure usage, and support marketing.{' '}
							<button
								type="button"
								onClick={() => setPrefsOpen(true)}
								className="text-neutral underline underline-offset-4 hover:no-underline">
								Manage
							</button>{' '}
							your cookie preferences anytime. Learn more about our{' '}
							<a
								href="/legal/cookie-policy"
								className="text-neutral underline underline-offset-4 hover:no-underline">
								cookie policy
							</a>
							.
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2 lg:shrink-0">
						<Button variant="ghost" size="md" onClick={() => setPrefsOpen(true)}>
							Cookie preferences
						</Button>
						<Button variant="outline" size="md" onClick={() => decide(false)}>
							Reject non-essential
						</Button>
						<Button variant="solid-strong" size="md" onClick={() => decide(true)}>
							Accept all
						</Button>
					</div>
				</div>
				<Button
					variant="ghost-muted"
					size="sm"
					iconOnly
					aria-label="Dismiss"
					onClick={() => setVisible(false)}
					className="absolute right-4 top-4">
					<X />
				</Button>
			</div>
			<CookiePreferencesDialog
				open={prefsOpen}
				onClose={() => setPrefsOpen(false)}
				onSaved={() => setVisible(false)}
			/>
		</>
	);
};
